export type TokenGroup =
  | 'Surfaces'
  | 'Text'
  | 'Accent'
  | 'Status'
  | 'Layout'
  | 'Sidebar'
  | 'Chrome (topbar)'
  | 'Main content'
  | 'Overlays (drawer / modal / popover)'

export interface TokenSpec {
  name: string
  label: string
  kind: 'color' | 'length'
  group: TokenGroup
  /** Baseline value from `tokens.css` (brand anchors resolved). Used as the
   * "factory default" — Theme Lab compares overrides against this instead of
   * reading `getComputedStyle`, so the baseline is stable even while
   * overrides are being applied to :root. Keep in sync with tokens.css. */
  default: string
}

export const TOKEN_GROUPS: TokenGroup[] = [
  'Surfaces',
  'Text',
  'Accent',
  'Status',
  'Layout',
  'Sidebar',
  'Chrome (topbar)',
  'Main content',
  'Overlays (drawer / modal / popover)',
]

/* ----------------------------------------------------------------------------
 * Contexts — the CSS scopes that re-declare base tokens. Selecting a context
 * in Theme Lab filters the token list to just the groups that apply there,
 * and surfaces the `remaps` so you can see which base tokens the context
 * shadows. Keep in sync with tokens.css context-mapping rules.
 * -------------------------------------------------------------------------- */

export type ContextKey = 'all' | 'base' | 'main' | 'sidebar' | 'chrome' | 'overlay'

export interface ContextSpec {
  key: ContextKey
  label: string
  /** CSS selector string, shown to the user as a hint. */
  selector: string
  /** Token groups surfaced when this context is selected. */
  groups: TokenGroup[]
  /** Base tokens the context re-declares — displayed as `--base → --scoped`. */
  remaps?: ReadonlyArray<{ from: string; to: string }>
  /** Short human description of what lives in this scope. */
  description?: string
}

export const CONTEXTS: ContextSpec[] = [
  {
    key: 'all',
    label: 'All',
    selector: ':root + every context',
    groups: TOKEN_GROUPS,
    description: 'Every token, every scope. Unfiltered.',
  },
  {
    key: 'base',
    label: 'Base',
    selector: ':root, .theme-night',
    groups: ['Surfaces', 'Text', 'Accent', 'Status', 'Layout'],
    description: 'Defaults inherited by every context unless re-declared below.',
  },
  {
    key: 'main',
    label: '.main',
    selector: '.main',
    groups: ['Main content'],
    remaps: [
      { from: '--bg-deep', to: '--main-bg-deep' },
      { from: '--panel', to: '--main-panel' },
      { from: '--panel-2', to: '--main-panel-2' },
      { from: '--line', to: '--main-line' },
    ],
    description: 'Page content surface. `--main-panel` is transparent by default.',
  },
  {
    key: 'sidebar',
    label: '.sidebar',
    selector: '.sidebar',
    groups: ['Sidebar'],
    remaps: [
      { from: '--bg', to: '--sidebar-bg' },
      { from: '--fg', to: '--sidebar-fg' },
      { from: '--fg-muted', to: '--sidebar-fg-muted' },
      { from: '--line', to: '--sidebar-line' },
    ],
    description: 'Left navigation column.',
  },
  {
    key: 'chrome',
    label: '.topbar / .brand',
    selector: '.topbar, .brand',
    groups: ['Chrome (topbar)'],
    remaps: [
      { from: '--bg', to: '--chrome-bg' },
      { from: '--fg', to: '--chrome-fg' },
      { from: '--line', to: '--chrome-line' },
    ],
    description: 'Topbar + brand chip — share one chrome surface.',
  },
  {
    key: 'overlay',
    label: '.drawer / .modal / .popover',
    selector: '.drawer, .modal, .popover',
    groups: ['Overlays (drawer / modal / popover)'],
    remaps: [
      { from: '--bg', to: '--overlay-bg' },
      { from: '--panel', to: '--overlay-bg' },
      { from: '--panel-2', to: '--overlay-panel-2' },
      { from: '--line', to: '--overlay-line' },
    ],
    description:
      'Floating overlays. Re-mapped so a transparent `--main-panel` does not leak in.',
  },
]

export const TOKENS: TokenSpec[] = [
  { name: '--bg-deep', label: 'Page background', kind: 'color', group: 'Surfaces', default: 'oklch(0.084 0.024 249)' },
  { name: '--bg', label: 'Chrome (topbar/sidebar)', kind: 'color', group: 'Surfaces', default: '#011627' },
  { name: '--bg-2', label: 'Chrome hover', kind: 'color', group: 'Surfaces', default: 'oklch(0.206 0.033 248)' },
  { name: '--panel', label: 'Panel', kind: 'color', group: 'Surfaces', default: '#011627' },
  { name: '--panel-2', label: 'Panel (nested)', kind: 'color', group: 'Surfaces', default: 'oklch(0.255 0.034 247)' },
  { name: '--line', label: 'Divider', kind: 'color', group: 'Surfaces', default: 'oklch(0.330 0.043 248)' },
  { name: '--line-strong', label: 'Divider (strong)', kind: 'color', group: 'Surfaces', default: 'oklch(0.403 0.061 247)' },
  { name: '--line-soft', label: 'Row highlight wash', kind: 'color', group: 'Surfaces', default: 'rgba(255,255,255,0.04)' },

  { name: '--fg', label: 'Foreground', kind: 'color', group: 'Text', default: 'oklch(0.950 0.013 245)' },
  { name: '--fg-muted', label: 'Foreground (muted)', kind: 'color', group: 'Text', default: 'oklch(0.767 0.032 247)' },
  { name: '--fg-faint', label: 'Foreground (faint)', kind: 'color', group: 'Text', default: 'oklch(0.575 0.043 248)' },

  { name: '--accent', label: 'Accent', kind: 'color', group: 'Accent', default: '#4fc3f7' },
  { name: '--accent-soft', label: 'Accent (soft)', kind: 'color', group: 'Accent', default: 'oklch(0.318 0.051 247)' },
  { name: '--accent-hover', label: 'Accent (hover)', kind: 'color', group: 'Accent', default: '#c9f0ff' },
  { name: '--accent-fg', label: 'On accent', kind: 'color', group: 'Accent', default: '#011627' },
  { name: '--on-accent-soft', label: 'On accent-soft', kind: 'color', group: 'Accent', default: 'oklch(0.950 0.013 245)' },

  { name: '--ok', label: 'OK', kind: 'color', group: 'Status', default: '#7be0b5' },
  { name: '--warn', label: 'Warn', kind: 'color', group: 'Status', default: 'oklch(0.82 0.16 75)' },
  { name: '--bad', label: 'Bad', kind: 'color', group: 'Status', default: 'oklch(0.72 0.18 25)' },
  { name: '--info', label: 'Info', kind: 'color', group: 'Status', default: '#4fc3f7' },

  { name: '--gap', label: 'Gap', kind: 'length', group: 'Layout', default: '12px' },
  { name: '--row', label: 'Row height', kind: 'length', group: 'Layout', default: '32px' },

  // Context-scoped tokens. Defaults match the base tokens (same hex values)
  // — overriding them only changes the corresponding region. Leave
  // untouched and the region follows the base palette.
  { name: '--sidebar-bg', label: 'Sidebar background', kind: 'color', group: 'Sidebar', default: '#011627' },
  { name: '--sidebar-fg', label: 'Sidebar text', kind: 'color', group: 'Sidebar', default: 'oklch(0.950 0.013 245)' },
  { name: '--sidebar-fg-muted', label: 'Sidebar text (muted)', kind: 'color', group: 'Sidebar', default: 'oklch(0.767 0.032 247)' },
  { name: '--sidebar-line', label: 'Sidebar divider', kind: 'color', group: 'Sidebar', default: 'oklch(0.330 0.043 248)' },

  { name: '--chrome-bg', label: 'Topbar/Brand bg', kind: 'color', group: 'Chrome (topbar)', default: '#011627' },
  { name: '--chrome-fg', label: 'Topbar text', kind: 'color', group: 'Chrome (topbar)', default: 'oklch(0.950 0.013 245)' },
  { name: '--chrome-line', label: 'Topbar divider', kind: 'color', group: 'Chrome (topbar)', default: 'oklch(0.330 0.043 248)' },

  { name: '--main-bg-deep', label: 'Main page bg', kind: 'color', group: 'Main content', default: 'oklch(0.084 0.024 249)' },
  { name: '--main-panel', label: 'Main panel bg', kind: 'color', group: 'Main content', default: 'transparent' },
  { name: '--main-panel-2', label: 'Main panel (nested)', kind: 'color', group: 'Main content', default: 'oklch(0.255 0.034 247)' },
  { name: '--main-line', label: 'Main divider', kind: 'color', group: 'Main content', default: 'oklch(0.330 0.043 248)' },

  { name: '--overlay-bg', label: 'Overlay surface', kind: 'color', group: 'Overlays (drawer / modal / popover)', default: '#011627' },
  { name: '--overlay-panel-2', label: 'Overlay nested', kind: 'color', group: 'Overlays (drawer / modal / popover)', default: 'oklch(0.255 0.034 247)' },
  { name: '--overlay-line', label: 'Overlay divider', kind: 'color', group: 'Overlays (drawer / modal / popover)', default: 'oklch(0.330 0.043 248)' },
]

/** Map of token name → baseline value for fast lookup. */
export const TOKEN_DEFAULTS: Record<string, string> = Object.fromEntries(
  TOKENS.map((t) => [t.name, t.default]),
)
